import { useCallback, useEffect, useRef } from 'react'

interface UseLongPressOptions {
    duration?: number
    onLongPress: () => void
}

interface UseLongPressReturn<T extends HTMLElement> {
    onPointerDown: (event: React.PointerEvent<T>) => void
    onPointerUp: (event: React.PointerEvent<T>) => void
    onPointerCancel: (event: React.PointerEvent<T>) => void
    onPointerLeave: (event: React.PointerEvent<T>) => void
    isLongPressTriggered: () => boolean
}

export function useLongPress<T extends HTMLElement = HTMLElement>(options: UseLongPressOptions): UseLongPressReturn<T> {
    const { duration = 500 } = options
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const longPressFiredRef = useRef(false)
    const onLongPressRef = useRef(options.onLongPress)

    // Refresh the callback on every render so the timer never invokes a stale closure
    onLongPressRef.current = options.onLongPress

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
    }, [])

    const cancel = useCallback(() => {
        clearTimer()
        longPressFiredRef.current = false
    }, [clearTimer])

    const onPointerDown = useCallback((event: React.PointerEvent<T>) => {
        // Only the primary pointer of the primary mouse button starts a long press
        // (touch and pen pointerdown report button 0 as well)
        if (event.button !== 0 || !event.isPrimary) {
            return
        }

        // Reset the click gate and drop any previous timer before starting a new one
        longPressFiredRef.current = false
        clearTimer()
        timerRef.current = setTimeout(() => {
            longPressFiredRef.current = true
            onLongPressRef.current()
        }, duration)
    }, [clearTimer, duration])

    const onPointerUp = useCallback(() => {
        // Stop the timer but keep the fired flag: pointerup fires before click and the
        // click handler still needs to know a long press happened. The flag is reset on
        // the next pointerdown/leave/cancel.
        clearTimer()
    }, [clearTimer])

    const onPointerCancel = useCallback(() => {
        cancel()
    }, [cancel])

    const onPointerLeave = useCallback(() => {
        cancel()
    }, [cancel])

    const isLongPressTriggered = useCallback(() => longPressFiredRef.current, [])

    useEffect(() => {
        // A window blur means the user left the page, so abandon the pending long press
        window.addEventListener('blur', cancel)
        return () => {
            window.removeEventListener('blur', cancel)
            clearTimer()
        }
    }, [cancel, clearTimer])

    return {
        onPointerDown,
        onPointerUp,
        onPointerCancel,
        onPointerLeave,
        isLongPressTriggered,
    }
}
